import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamChampionshipWeeksCard from '../../ChampionshipWeeks/TeamChampionshipWeeksCard';
import ChampionshipMatchupCompareModal from '../../ChampionshipWeeks/ChampionshipMatchupCompareModal';
import { prefetchTeamLogos, TEAM_LOGO_CACHE_SIZE } from '../../../utils/teamLogoCache';

const PLAYOFF_WEEKS = [15, 16, 17];
const SURVIVAL_WEEKS = Array.from({ length: 14 }, (_, index) => index + 1);

function getOpponentIds(team, weeks) {
	return weeks
		.map((week) => {
			const matchup = team.weeks?.[week] || team.weeks?.[String(week)];
			return matchup?.opponent_id;
		})
		.filter(Boolean);
}

function buildEndpointWithMode(endpoint, mode) {
	if (!endpoint) return endpoint;
	const separator = endpoint.includes('?') ? '&' : '?';
	return `${endpoint}${separator}mode=${encodeURIComponent(mode)}`;
}

export default function ChampionshipWeeksPage({
	get_championship_weeks_endpoint,
	get_championship_weeks_team_players_endpoint
}) {
	const [mode, setMode] = useState('championship');
	const [weeks, setWeeks] = useState(PLAYOFF_WEEKS);
	const [teams, setTeams] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState('');
	const [search, setSearch] = useState('');
	const [teamFilter, setTeamFilter] = useState([]);
	const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
	const teamDropdownRef = useRef(null);
	const [conferenceFilter, setConferenceFilter] = useState('all');
	const [divisionFilter, setDivisionFilter] = useState('all');
	const [expandedTeamIds, setExpandedTeamIds] = useState(() => new Set());
	const [pendingScroll, setPendingScroll] = useState(null);
	const [playerSlots, setPlayerSlots] = useState({});
	const [selectedMatchup, setSelectedMatchup] = useState(null);

	const isSurvival = mode === 'survival';
	const activeWeeks = weeks.length > 0 ? weeks : isSurvival ? SURVIVAL_WEEKS : PLAYOFF_WEEKS;

	useEffect(() => {
		let cancelled = false;

		async function loadMap() {
			setIsLoading(true);
			setLoadError('');
			setExpandedTeamIds(new Set());
			setSelectedMatchup(null);
			setPlayerSlots({});

			try {
				const url = buildEndpointWithMode(get_championship_weeks_endpoint, mode);
				const response = await fetch(url, {
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				});
				const payload = await response.json();
				if (!response.ok || !payload.success) {
					throw new Error(
						payload.error ||
							(mode === 'survival'
								? 'Could not load survival weeks.'
								: 'Could not load championship weeks.')
					);
				}
				if (!cancelled) {
					const nextTeams = payload.data?.teams || [];
					setTeams(nextTeams);
					prefetchTeamLogos(
						nextTeams.map((team) => team.image_url),
						TEAM_LOGO_CACHE_SIZE
					);
					setWeeks(
						Array.isArray(payload.data?.weeks) && payload.data.weeks.length > 0
							? payload.data.weeks
							: mode === 'survival'
								? SURVIVAL_WEEKS
								: PLAYOFF_WEEKS
					);
				}
			} catch (error) {
				if (!cancelled) {
					setLoadError(
						error.message ||
							(mode === 'survival'
								? 'Could not load survival weeks.'
								: 'Could not load championship weeks.')
					);
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		}

		loadMap();
		return () => {
			cancelled = true;
		};
	}, [get_championship_weeks_endpoint, mode]);

	const divisions = useMemo(() => {
		const values = new Set(teams.map((team) => team.division).filter(Boolean));
		return Array.from(values).sort();
	}, [teams]);

	const teamsById = useMemo(() => {
		return teams.reduce((lookup, team) => {
			lookup[team.id] = team;
			return lookup;
		}, {});
	}, [teams]);

	const filteredTeams = useMemo(() => {
		const query = search.trim().toLowerCase();
		return teams.filter((team) => {
			if (teamFilter.length > 0 && !teamFilter.includes(String(team.id))) return false;
			if (conferenceFilter !== 'all' && team.conference !== conferenceFilter) return false;
			if (divisionFilter !== 'all' && team.division !== divisionFilter) return false;
			if (!query) return true;

			const haystack = [
				team.name,
				team.abbreviation,
				...activeWeeks.flatMap((week) => {
					const matchup = team.weeks?.[week] || team.weeks?.[String(week)];
					return [matchup?.opponent_name, matchup?.opponent_abbreviation];
				})
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase();

			return haystack.includes(query);
		});
	}, [teams, search, teamFilter, conferenceFilter, divisionFilter, activeWeeks]);

	const sortedTeams = useMemo(
		() => [...teams].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
		[teams]
	);

	const loadTeamPlayers = useCallback(
		async (teamId) => {
			if (!teamId) return;

			setPlayerSlots((prev) => {
				const existing = prev[teamId];
				if (existing && (existing.loading || existing.players)) {
					return prev;
				}
				return { ...prev, [teamId]: { loading: true, error: '', players: null } };
			});

			try {
				const url = get_championship_weeks_team_players_endpoint.replace(
					':team_id',
					String(teamId)
				);
				const response = await fetch(url, {
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				});
				const payload = await response.json();
				if (!response.ok || !payload.success) {
					throw new Error(payload.error || 'Could not load team players.');
				}
				setPlayerSlots((prev) => ({
					...prev,
					[teamId]: {
						loading: false,
						error: '',
						players: payload.data?.players || []
					}
				}));
			} catch (error) {
				setPlayerSlots((prev) => ({
					...prev,
					[teamId]: {
						loading: false,
						error: error.message || 'Could not load team players.',
						players: null
					}
				}));
			}
		},
		[get_championship_weeks_team_players_endpoint]
	);

	const handleToggleExpand = useCallback(
		(teamId) => {
			setExpandedTeamIds((current) => {
				const next = new Set(current);
				if (next.has(teamId)) {
					next.delete(teamId);
					return next;
				}

				next.add(teamId);
				const team = teamsById[teamId];
				if (team) {
					loadTeamPlayers(teamId);
					getOpponentIds(team, activeWeeks).forEach((opponentId) =>
						loadTeamPlayers(opponentId)
					);
				}
				setPendingScroll({ teamId, key: Date.now() });
				return next;
			});
		},
		[teamsById, loadTeamPlayers, activeWeeks]
	);

	useEffect(() => {
		if (!pendingScroll) return undefined;

		const frame = requestAnimationFrame(() => {
			const card = document.getElementById(
				`championship-team-card-${pendingScroll.teamId}`
			);
			if (!card) return;

			const cardTop = card.getBoundingClientRect().top + window.pageYOffset;
			const offset = 16;
			window.scrollTo({
				top: Math.max(0, cardTop - offset),
				behavior: 'smooth'
			});
		});

		return () => cancelAnimationFrame(frame);
	}, [pendingScroll]);

	const handleWeekClick = useCallback(
		(team, week, matchup) => {
			const opponent = teamsById[matchup.opponent_id];
			if (!opponent) return;

			setSelectedMatchup({ team, opponent, week, matchup });
		},
		[teamsById]
	);

	const handleCloseMatchupCompare = () => {
		setSelectedMatchup(null);
	};

	const handleTeamFilterToggle = useCallback((teamId) => {
		const id = String(teamId);
		setTeamFilter((prev) =>
			prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
		);
	}, []);

	useEffect(() => {
		if (!isTeamDropdownOpen) return undefined;

		const handleClickOutside = (event) => {
			if (
				teamDropdownRef.current &&
				!teamDropdownRef.current.contains(event.target)
			) {
				setIsTeamDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isTeamDropdownOpen]);

	const teamFilterLabel = useMemo(() => {
		if (teamFilter.length === 0) return 'All';
		const selectedNames = teamFilter
			.map((id) => teamsById[id]?.abbreviation || teamsById[id]?.name)
			.filter(Boolean);
		return selectedNames.join(', ');
	}, [teamFilter, teamsById]);

	const pageTitle = isSurvival ? 'Survival Weeks' : 'Championship Weeks';
	const loadingLabel = isSurvival
		? 'Loading survival schedules…'
		: 'Loading championship schedules…';

	return (
		<div className="container p-4 pt-4 lg:py-16 md:px-20">
			<div className="mb-3 lg:mb-6 flex items-center justify-between gap-4">
				<h1 className="min-w-0 text-xl sm:text-2xl md:text-4xl font-semibold leading-none text-gray-900 dark:text-dark-text">
					{pageTitle}
				</h1>
				<Link
					to="/best-ball-zone"
					className="flex-shrink-0 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Best Ball Zone
				</Link>
			</div>

			<div className="inline-flex rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-1 shadow-sm">
				<button
					type="button"
					onClick={() => setMode('championship')}
					className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
						!isSurvival
							? 'bg-blue-600 text-white'
							: 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg'
					}`}
				>
					Championship
				</button>
				<button
					type="button"
					onClick={() => setMode('survival')}
					className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
						isSurvival
							? 'bg-blue-600 text-white'
							: 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg'
					}`}
				>
					Survival
				</button>
			</div>

			<div className="mt-5 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-4 shadow-sm">
				<div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
					<div className="flex-1 w-full">
						<label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Search team or opponent
						</label>
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Denver, KC, Eagles…"
							className="mt-1 w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text"
						/>
					</div>
					<div className="relative" ref={teamDropdownRef}>
						<label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Team
						</label>
						<button
							type="button"
							onClick={() => setIsTeamDropdownOpen((prev) => !prev)}
							className="mt-1 w-full lg:w-56 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text flex items-center justify-between gap-2"
						>
							<span
								className={`truncate text-left ${teamFilter.length === 0
									? 'text-gray-400 dark:text-gray-400'
									: ''}`}
							>
								{teamFilter.length === 0 ? 'All' : teamFilterLabel}
							</span>
							<i className="fa fa-chevron-down text-xs text-gray-500 dark:text-gray-400" />
						</button>
						{isTeamDropdownOpen && (
							<div className="absolute z-20 mt-1 w-full lg:w-64 max-h-60 overflow-y-auto rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card shadow-lg">
								{teamFilter.length > 0 && (
									<button
										type="button"
										onClick={() => setTeamFilter([])}
										className="w-full text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-dark-bg border-b border-gray-200 dark:border-dark-border"
									>
										Clear selection
									</button>
								)}
								<div className="p-2">
									{sortedTeams.map((team) => {
										const id = String(team.id);
										const checked = teamFilter.includes(id);
										return (
											<label
												key={team.id}
												htmlFor={`championship-team-${id}`}
												className="flex items-center gap-2 px-1 py-1 text-sm text-gray-800 dark:text-dark-text cursor-pointer rounded hover:bg-gray-50 dark:hover:bg-dark-bg"
											>
												<input
													type="checkbox"
													id={`championship-team-${id}`}
													value={id}
													checked={checked}
													onChange={() => handleTeamFilterToggle(id)}
													className="accent-blue-600"
												/>
												<span className="truncate">{team.name}</span>
											</label>
										);
									})}
								</div>
							</div>
						)}
					</div>
					<div>
						<label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Conference
						</label>
						<select
							value={conferenceFilter}
							onChange={(event) => setConferenceFilter(event.target.value)}
							className="mt-1 w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text"
						>
							<option value="all">All</option>
							<option value="AFC">AFC</option>
							<option value="NFC">NFC</option>
						</select>
					</div>
					<div>
						<label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Division
						</label>
						<select
							value={divisionFilter}
							onChange={(event) => setDivisionFilter(event.target.value)}
							className="mt-1 w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text"
						>
							<option value="all">All</option>
							{divisions.map((division) => (
								<option key={division} value={division}>
									{division}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			{!isLoading && !loadError && filteredTeams.length > 0 && (
				<div className="mt-4 hidden md:block h-3" aria-hidden="true" />
			)}

			{isLoading && (
				<div className="mt-6 flex items-center justify-center py-20 text-sm text-gray-500 dark:text-gray-400">
					{loadingLabel}
				</div>
			)}

			{loadError && (
				<div className="mt-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900 dark:bg-opacity-20 p-4 text-sm text-red-700 dark:text-red-300">
					{loadError}
				</div>
			)}

			{!isLoading && !loadError && (
				<div className="mt-2 flex flex-col gap-4 lg:gap-5">
					{filteredTeams.map((team) => (
						<TeamChampionshipWeeksCard
							key={`${mode}-${team.id}`}
							team={team}
							teamsById={teamsById}
							weeks={activeWeeks}
							isExpanded={!isSurvival && expandedTeamIds.has(team.id)}
							playerSlots={playerSlots}
							onToggleExpand={isSurvival ? undefined : handleToggleExpand}
							onWeekClick={
								isSurvival
									? undefined
									: (week, matchup) => handleWeekClick(team, week, matchup)
							}
							interactive={!isSurvival}
							showExpand={!isSurvival}
						/>
					))}
				</div>
			)}

			{!isLoading && !loadError && filteredTeams.length === 0 && (
				<div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
					No teams match your filters.
				</div>
			)}

			{!isSurvival && (
				<ChampionshipMatchupCompareModal
					isOpen={Boolean(selectedMatchup)}
					onClose={handleCloseMatchupCompare}
					team={selectedMatchup?.team}
					opponent={selectedMatchup?.opponent}
					week={selectedMatchup?.week}
					matchup={selectedMatchup?.matchup}
				/>
			)}
		</div>
	);
}
